import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { DayPicker } from "react-day-picker";
import { useController, useFormContext } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import moment from "moment";

import "react-day-picker/dist/style.css";
import { ReactComponent as CalendarIcon } from "assets/images/iconsV2/calendar.svg";
import { ReactComponent as XCircleGrey } from "assets/images/iconsV2/x-circle-grey.svg";
import { classNames } from "helpers/commonHelpers";
import { getPackingListFieldPath } from "@RequestFormV2/sections/PackingList/utils/fieldPaths";
import {
  STORAGE_FORMAT,
  DISPLAY_FORMAT,
  parseStoredDate,
  applyDateMask,
  getGhostText,
  parseDateFromDigits,
  formatStoredToInput,
} from "@RequestFormV2/sections/PackingList/components/shared/cratingDatePickerUtils";
import useDatePickerPopup from "@RequestFormV2/sections/PackingList/components/shared/useDatePickerPopup";

import "./PackingListCratingDatePicker.scss";

type PackingListCratingDatePickerProps = {
  index: number;
  onChange: () => void;
};

export default function PackingListCratingDatePicker({ index, onChange }: PackingListCratingDatePickerProps) {
  const { control, setValue } = useFormContext();
  const fieldName = getPackingListFieldPath(index, "crating_required_by");
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { isOpen, popupStyle, containerRef, popupRef, setPopupRef, openPopup, closePopup } = useDatePickerPopup();

  const { field, fieldState } = useController({
    name: fieldName,
    control,
  });
  const hasError = Boolean(fieldState.error);

  const initialInput = useMemo(() => formatStoredToInput(field.value), [field.value]);
  const [inputValue, setInputValue] = useState(initialInput.inputValue);
  const [rawDigits, setRawDigits] = useState(initialInput.rawDigits);

  const selectedDate = useMemo(() => parseStoredDate(field.value), [field.value]);
  const [month, setMonth] = useState<Date>(() => selectedDate ?? new Date());
  const hasCommittedDate = Boolean(field.value);
  const showClear = (isFocused || isOpen) && (hasCommittedDate || Boolean(inputValue));

  useEffect(() => {
    const nextInput = formatStoredToInput(field.value);
    setInputValue(nextInput.inputValue);
    setRawDigits(nextInput.rawDigits);
  }, [field.value]);

  useEffect(() => {
    if (selectedDate) setMonth(selectedDate);
  }, [selectedDate]);

  const commitDate = useCallback(
    (date: Date) => {
      const formatted = moment(date).format(STORAGE_FORMAT);
      const displayValue = moment(date).format(DISPLAY_FORMAT);
      field.onChange(formatted);
      setValue(getPackingListFieldPath(index, "crating_required"), true);
      setInputValue(displayValue);
      setRawDigits(displayValue.replace(/\D/g, ""));
      onChange();
    },
    [field, index, onChange, setValue],
  );

  const revertInput = useCallback(() => {
    const nextInput = formatStoredToInput(field.value);
    setInputValue(nextInput.inputValue);
    setRawDigits(nextInput.rawDigits);
  }, [field.value]);

  const handleSelect = useCallback(
    (date: Date | undefined) => {
      if (!date) return;
      commitDate(date);
      closePopup();
    },
    [closePopup, commitDate],
  );

  const handleClear = useCallback(() => {
    field.onChange("");
    setValue(getPackingListFieldPath(index, "crating_required"), false);
    setInputValue("");
    setRawDigits("");
    onChange();
    closePopup();
    inputRef.current?.focus();
  }, [closePopup, field, index, onChange, setValue]);

  const handleCalendarClick = useCallback(() => {
    openPopup();
    inputRef.current?.focus();
  }, [openPopup]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 8);
    setRawDigits(digits);
    setInputValue(applyDateMask(digits));

    if (digits.length >= 4) {
      const monthIndex = parseInt(digits.slice(2, 4), 10) - 1;
      const year = digits.length >= 8 ? parseInt(digits.slice(4, 8), 10) : new Date().getFullYear();
      if (monthIndex >= 0 && monthIndex <= 11 && year >= 1000) {
        setMonth(new Date(year, monthIndex, 1));
      }
    }
  };

  const handleInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    field.onBlur();

    const relatedTarget = event.relatedTarget as Node | null;
    if (containerRef.current?.contains(relatedTarget) || popupRef.current?.contains(relatedTarget)) {
      return;
    }

    closePopup();
    setIsFocused(false);

    if (!rawDigits) {
      field.onChange("");
      setValue(getPackingListFieldPath(index, "crating_required"), false);
      setInputValue("");
      onChange();
      return;
    }

    const parsedDate = parseDateFromDigits(rawDigits);
    if (parsedDate) {
      commitDate(parsedDate);
      return;
    }

    revertInput();
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    openPopup();
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Tab" && isOpen) {
      closePopup();
    }
  };

  const ghostText = getGhostText(inputValue);

  return (
    <div
      ref={containerRef}
      className={classNames("rfv2-packing-list-crating-datepicker", {
        "rfv2-packing-list-crating-datepicker--open": isOpen,
        "rfv2-packing-list-crating-datepicker--error": hasError,
      })}
    >
      <div className="rfv2-packing-list-crating-datepicker__trigger">
        <button
          type="button"
          tabIndex={-1}
          className="rfv2-packing-list-crating-datepicker__calendar-button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={handleCalendarClick}
          aria-label="Open calendar"
          aria-expanded={isOpen}
          aria-haspopup="dialog"
        >
          <CalendarIcon className="rfv2-packing-list-crating-datepicker__icon" />
        </button>
        <div className="rfv2-packing-list-crating-datepicker__input-field">
          <input
            ref={(node) => {
              inputRef.current = node;
              field.ref(node);
            }}
            type="text"
            name={field.name}
            className="rfv2-packing-list-crating-datepicker__input"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            onKeyDown={handleInputKeyDown}
            autoComplete="off"
            inputMode="numeric"
          />
          <span className="rfv2-packing-list-crating-datepicker__ghost" aria-hidden="true">
            <span className="rfv2-packing-list-crating-datepicker__ghost-invisible">{ghostText.invisible}</span>
            <span className="rfv2-packing-list-crating-datepicker__ghost-visible">{ghostText.visible}</span>
          </span>
        </div>
      </div>

      {hasError && (
        <div className="text-sm error-text text-error-primary">
          {fieldState.error?.message || <FormattedMessage id="general.required" defaultMessage="Required" />}
        </div>
      )}

      {showClear && (
        <button
          type="button"
          tabIndex={-1}
          className="rfv2-packing-list-crating-datepicker__clear"
          onMouseDown={(event) => event.preventDefault()}
          onClick={handleClear}
          aria-label="Clear crating date"
        >
          <XCircleGrey />
        </button>
      )}

      {isOpen &&
        createPortal(
          <div
            ref={setPopupRef}
            className="rfv2-packing-list-crating-datepicker__popup"
            style={popupStyle}
            onMouseDown={(event) => event.preventDefault()}
          >
            <div className="date-picker-outer-container">
              <DayPicker
                mode="single"
                selected={selectedDate}
                onSelect={handleSelect}
                numberOfMonths={1}
                month={month}
                onMonthChange={setMonth}
              />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
